import type { Lang } from "./config";

/**
 * Central karta över hur varje publik sida heter i de tre språken.
 * Driver språkväljarens "samma sida, annat språk"-länk och hreflang.
 *
 * Nyckel = svensk route-id (utan inledande slash), värde = URL per språk
 * (svanska har ingen prefix, en/es har /en, /es).
 */
export type SlugMap = Record<Lang, string>;

export const PAGE_SLUGS: Record<string, SlugMap> = {
  "index": { sv: "/", en: "/en", es: "/es" },
  "annonser.index": { sv: "/annonser", en: "/en/listings", es: "/es/anuncios" },
  "annonser.$slug": { sv: "/annonser/:slug", en: "/en/listings/:slug", es: "/es/anuncios/:slug" },
  "a.$slug": { sv: "/a/:slug", en: "/a/:slug", es: "/a/:slug" },
  // Kategorisidorna finns bara på svenska adress – samma URL i alla språk.
  "salja.$kategori": { sv: "/salja/:kategori", en: "/salja/:kategori", es: "/salja/:kategori" },
  "kopa-och-salja.index": { sv: "/kopa-och-salja", en: "/en/buy-and-sell", es: "/es/comprar-y-vender" },
  "kopa-och-salja.$ort": { sv: "/kopa-och-salja/:ort", en: "/en/buy-and-sell/:ort", es: "/es/comprar-y-vender/:ort" },
  "priser": { sv: "/priser", en: "/en/pricing", es: "/es/precios" },
  "sa-fungerar-det": { sv: "/sa-fungerar-det", en: "/en/how-it-works", es: "/es/como-funciona" },
  "videoskanning": { sv: "/videoskanning", en: "/en/video-scan", es: "/es/escaneo-video" },
  "om": { sv: "/om", en: "/en/about", es: "/es/sobre-nosotros" },
  "tips": { sv: "/tips", en: "/en/tips", es: "/es/consejos" },
  "guider.index": { sv: "/guider", en: "/en/guides", es: "/es/guias" },
  "guider.$slug": { sv: "/guider/:slug", en: "/en/guides/:slug", es: "/es/guias/:slug" },
  "jamfor.index": { sv: "/jamfor", en: "/en/compare", es: "/es/comparar" },
  "jamfor.$tjanst": { sv: "/jamfor/:tjanst", en: "/en/compare/:tjanst", es: "/es/comparar/:tjanst" },
  "foretag.index": { sv: "/foretag", en: "/en/businesses", es: "/es/empresas" },
  "foretag.lista": { sv: "/foretag", en: "/en/businesses", es: "/es/empresas" },
  "foretag.$slug": { sv: "/foretag/:slug", en: "/en/businesses/:slug", es: "/es/empresas/:slug" },
  "support": { sv: "/support", en: "/en/support", es: "/es/soporte" },
  "anvandarvillkor": { sv: "/anvandarvillkor", en: "/en/terms", es: "/es/terminos" },
  "integritetspolicy": { sv: "/integritetspolicy", en: "/en/privacy", es: "/es/privacidad" },
  "cookiepolicy": { sv: "/cookiepolicy", en: "/en/cookies", es: "/es/cookies" },
  "regler": { sv: "/regler", en: "/en/rules", es: "/es/reglas" },
  "varfor-kostar-det": { sv: "/varfor-kostar-det", en: "/en/why-it-costs", es: "/es/por-que-cuesta" },
  "glomt-losenord": { sv: "/glomt-losenord", en: "/en/forgot-password", es: "/es/olvide-contrasena" },
  "skapa": { sv: "/skapa", en: "/en/create", es: "/es/crear" },
};

/**
 * Ger URL:en för samma sida i annat språk. `path` är den aktuella URL:en
 * (pathname). Okända sidor får bara /en resp. /es-startsidan som fallback.
 */
export function siblingLangPath(path: string, lang: Lang): string {
  const match = Object.values(PAGE_SLUGS).find((paths) =>
    Object.values(paths).some((candidate) => candidate === path),
  );
  if (match) return match[lang];

  // Dynamiska språkvägar matchas mot kartans :param-mönster.
  for (const paths of Object.values(PAGE_SLUGS)) {
    for (const candidate of Object.values(paths)) {
      const names = [...candidate.matchAll(/:([^/]+)/g)].map((item) => item[1]);
      if (!names.length) continue;
      const pattern = new RegExp(`^${candidate.replace(/:[^/]+/g, "([^/]+)")}$`);
      const values = path.match(pattern)?.slice(1);
      if (!values) continue;
      let destination = paths[lang];
      names.forEach((name, index) => {
        destination = destination.replace(`:${name}`, values[index]);
      });
      return destination;
    }
  }

  if (lang === "sv") return path.replace(/^\/(en|es)(\/|$)/, "/");
  const bare = path.replace(/^\/(en|es)(?=\/|$)/, "");
  const norm = bare === "" ? "index" : bare.slice(1);
  // Dynamiska sidor (annons/kategori/stad) saknar entydig karta: gå till
  // motsvarande sektionssida i nytt språk.
  const head = norm.split("/")[0];
  if (head === "annonser") return PAGE_SLUGS["annonser.index"][lang];
  if (head === "salja") return `/${norm}`;
  if (head === "kopa-och-salja") return PAGE_SLUGS["kopa-och-salja.index"][lang];
  if (head === "guider") return PAGE_SLUGS["guider.index"][lang];
  if (head === "jamfor") return PAGE_SLUGS["jamfor.index"][lang];
  if (head === "foretag") return PAGE_SLUGS["foretag.index"][lang];
  if (head === "a") return PAGE_SLUGS["a.$slug"][lang];
  if (norm === "index") return PAGE_SLUGS["index"][lang];
  // Sidor utan egen språkadress (t.ex. inloggade kontosidor) behåller sin URL.
  return `/${norm}`;
}

/** Ger rätt publik adress för ett känt svenskt mål på aktuellt språk. */
export function localizedPath(path: string, lang: Lang): string {
  if (!path.startsWith("/")) return path;

  const [pathname, suffix = ""] = path.split(/(?=[?#])/u, 2);
  return `${siblingLangPath(pathname, lang)}${suffix}`;
}
